import { useEffect, useState } from 'react';
import { useMediaQuery, Dialog, Slide, Box } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { useAccount } from '../../../contexts/Account';
import { getAdvisorRecurlyPlan } from '../../../services/advisor_services';
import eventBus, { eventBusValues } from '../../../eventBus';
import Step1 from './Step1';
import Step2 from './Step2';
import { sendFSEventUpgradeViewed } from '../../../services/FullStory';

const FreemiumModal = ({ onUpgradeSuccess }) => {
  const {
    accountDetails,
    currentPlan,
    asLoggedUserDetails,
    currentUserInfo,
    type,
  } = useAccount();
  const isLess900 = useMediaQuery((theme) => theme.breakpoints.down(900));
  
  const [openModal, setOpenModal] = useState(false);
  const [step1, setStep1] = useState(true);
  const [monthlyUpgrade1, setMonthlyUpgrade1] = useState({});
  const [monthlyUpgrade2, setMonthlyUpgrade2] = useState({});
  const [monthlyUpgrade3, setMonthlyUpgrade3] = useState({});
  const [annualUpgrade1, setAnnualUpgrade1] = useState({});
  const [annualUpgrade2, setAnnualUpgrade2] = useState({});
  const [annualUpgrade3, setAnnualUpgrade3] = useState({});
  const [planData, setPlanData] = useState({});
  const [fromBanner, setFromBanner] = useState(false);

  const handleContinue = () => {
    setStep1(false);
  };
  const handleBack = () => {
    setStep1(true);
  };

  useEffect(() => {
    async function fetchData() {
      const [
        monthly1Response,
        monthly2Response,
        monthly3Response,
        annual1Response,
        annual2Response,
        annual3Response,
      ] = await Promise.all([
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumMonthlyUpgrade1,
        }),
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumMonthlyUpgrade2,
        }),
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumMonthlyUpgrade3,
        }),
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumAnnualUpgrade1,
        }),
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumAnnualUpgrade2,
        }),
        getAdvisorRecurlyPlan({
          recurlyPlanCode: currentPlan.b2cFreemiumAnnualUpgrade3,
        }),
      ]);
      if (monthly1Response.data && monthly1Response.data.getRecurlyPlan) {
        setMonthlyUpgrade1(monthly1Response.data.getRecurlyPlan);
      }
      if (monthly2Response.data && monthly2Response.data.getRecurlyPlan) {
        setMonthlyUpgrade2(monthly2Response.data.getRecurlyPlan);
      }
      if (monthly3Response.data && monthly3Response.data.getRecurlyPlan) {
        setMonthlyUpgrade3(monthly3Response.data.getRecurlyPlan);
      }
      if (annual1Response.data && annual1Response.data.getRecurlyPlan) {
        setAnnualUpgrade1(annual1Response.data.getRecurlyPlan);
      }
      if (annual2Response.data && annual2Response.data.getRecurlyPlan) {
        setAnnualUpgrade2(annual2Response.data.getRecurlyPlan);
      }
      if (annual3Response.data && annual3Response.data.getRecurlyPlan) {
        setAnnualUpgrade3(annual3Response.data.getRecurlyPlan);
      }
    }

    eventBus.on(eventBusValues.triggerAdvisorUpgradeAction, (data) => {
      setFromBanner(data ? data.fromBanner : false);
      setOpenModal(true);
    });

    fetchData();
    return () => {
      eventBus.remove(eventBusValues.triggerAdvisorUpgradeAction);
    };
  }, []);

  function handleClose() {
    setOpenModal(false);
    if (monthlyUpgrade2) {
      setStep1(true);
    }
  }

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freemium',
      upgradeAttempted,
    });
  };

  const handleOptionChange = (options, data, annualToggle) => {
    setPlanData({ options, data, annualToggle });
  };

  return (
    <div>
      <Dialog
        open={openModal}
        fullScreen
        scroll="paper"
        className="not-scrollable"
        onClose={() => {
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'advisorFreemiumUpgradeModal',
          sx: {
            borderRadius: '0px',
            boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0.90) 20%, #D3DEFF 45%, #D9F1FF 70%, rgba(255, 255, 255, 0.90) 85%)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xxs: '0px', sm: '40px' },
            overflowY: step1 ? 'hidden' : 'auto',
          },
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          <Clear
            onClick={() => {
              handleClose();
              sendFSViewedEvent();
            }}
          />
        </div>

        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: '1024px',
            minHeight: isLess900 ? '100%' : '620px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowX: 'hidden',
            overflowY: 'visible',
          }}
        >
          <Slide direction="right" in={step1} mountOnEnter unmountOnExit>
            <Box
              sx={{
                width: '100%',
                position: 'absolute',
                top: 0,
                left: step1 ? 0 : '-100vw',
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: step1 ? 'auto' : 'none',
              }}
            >
              <Step1
                onContinue={handleContinue}
                month1={monthlyUpgrade1}
                month2={monthlyUpgrade2}
                month3={monthlyUpgrade3}
                annual1={annualUpgrade1}
                annual2={annualUpgrade2}
                annual3={annualUpgrade3}
                onOptionChange={handleOptionChange}
                onClose={() => {
                  handleClose();
                  sendFSViewedEvent();
                }}
              />
            </Box>
          </Slide>
          <Slide
            direction="left"
            in={!step1}
            mountOnEnter
            unmountOnExit
            timeout={800}
          >
            <Box
              sx={{
                width: '100%',
                height: '600px',
                position: 'absolute',
                top: 0,
                left: step1 ? '100vw' : 0,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: step1 ? 'none' : 'auto',
              }}
            >
              <Step2
                planData={planData}
                onPlanChange={handleOptionChange}
                goBack={handleBack}
                closeModal={handleClose}
                onUpgradeSuccess={onUpgradeSuccess}
                fromBanner={fromBanner}
                isSingleOption={false}
              />
            </Box>
          </Slide>
        </Box>
      </Dialog>
    </div>
  );
};

export default FreemiumModal;
